// Engagement polls / pulse (Phase 5b) — role-adaptive. Employees vote on open
// polls and see results; HR creates polls and reviews results.
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { Plus, BarChart2, Trash2 } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, Pill } from './ui';
import { EmptyState } from './components';
import { AG } from './theme/tokens';
import { api } from './api';
import { useAuth } from './auth';

function Results({ results, total, myChoice }) {
  return (
    <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
      {results.map((r, i) => (
        <div key={i}>
          <div className="flex items-center justify-between" style={{ fontSize: 13, marginBottom: 3 }}>
            <span style={{ color: AG.text, fontWeight: i === myChoice ? 700 : 500 }}>{r.option}{i === myChoice ? ' ✓' : ''}</span>
            <span style={{ color: AG.n500 }}>{r.pct}% · {r.count}</span>
          </div>
          <div style={{ height: 7, borderRadius: 6, background: AG.n100, overflow: 'hidden' }}>
            <div style={{ width: `${r.pct}%`, height: '100%', background: i === myChoice ? AG.primary : AG.primaryLight }} />
          </div>
        </div>
      ))}
      <div style={{ fontSize: 11, color: AG.n400 }}>{total} {total === 1 ? 'response' : 'responses'}</div>
    </div>
  );
}

export default function Pulse({ showToast }) {
  const { isHR } = useAuth();
  return isHR ? <PulseHR showToast={showToast} /> : <PulseEmployee showToast={showToast} />;
}

function PulseEmployee({ showToast }) {
  const [rows, setRows] = useState(null);
  const load = () => api.openPolls().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const vote = async (id, choice) => { try { await api.votePoll(id, choice); load(); } catch (e) { showToast?.(e.message); } };
  if (!rows) return <CenterSpinner />;
  return (
    <div className="space-y-4 fade-up">
      <SectionTitle title="Pulse" sub="Quick polls from your team" />
      {rows.length === 0 ? <Card><EmptyState icon={BarChart2} title="No open polls" desc="New pulse polls will appear here." /></Card> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {rows.map((p) => (
            <Card key={p.id} style={{ padding: 18 }}>
              <div style={{ fontWeight: 600, color: AG.text, marginBottom: 4 }}>{p.question}</div>
              {p.voted ? <Results results={p.results} total={p.total} myChoice={p.myChoice} /> : (
                <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
                  {p.options.map((o, i) => <OutlineBtn key={i} onClick={() => vote(p.id, i)}>{o}</OutlineBtn>)}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function PulseHR({ showToast }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.pollsAll().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const create = async () => {
    const options = form.options.map((o) => o.trim()).filter(Boolean);
    if (!form.question.trim() || options.length < 2 || busy) return; setBusy(true);
    try { await api.createPoll({ question: form.question, options }); showToast?.('Poll created'); setForm(null); load(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const close = async (id) => { try { await api.closePoll(id); load(); } catch (e) { showToast?.(e.message); } };
  if (!rows) return <CenterSpinner />;
  return (
    <div className="space-y-4 fade-up">
      <div className="flex items-center justify-between" style={{ gap: 12, flexWrap: 'wrap' }}>
        <SectionTitle title="Pulse" sub="Engagement polls & results" />
        <PrimaryBtn onClick={() => setForm({ question: '', options: ['', ''] })}><Plus size={14} style={{ marginRight: 6 }} /> New poll</PrimaryBtn>
      </div>
      {rows.length === 0 ? <Card><EmptyState icon={BarChart2} title="No polls yet" desc="Create a quick pulse poll for your team." /></Card> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {rows.map((p) => (
            <Card key={p.id} style={{ padding: 18 }}>
              <div className="flex items-start justify-between" style={{ gap: 8 }}>
                <div style={{ fontWeight: 600, color: AG.text }}>{p.question}</div>
                <Pill tone={p.status === 'Open' ? 'info' : 'neutral'}>{p.status}</Pill>
              </div>
              <Results results={p.results} total={p.total} myChoice={-1} />
              {p.status === 'Open' && <div style={{ marginTop: 10 }}><OutlineBtn onClick={() => close(p.id)}>Close poll</OutlineBtn></div>}
            </Card>
          ))}
        </div>
      )}
      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>New pulse poll</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <TextField fullWidth size="small" label="Question" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} sx={{ mb: 1.5 }} />
              {form.options.map((o, i) => (
                <div key={i} className="flex items-center gap-1" style={{ marginBottom: 8 }}>
                  <TextField fullWidth size="small" label={`Option ${i + 1}`} value={o} onChange={(e) => setForm({ ...form, options: form.options.map((x, j) => (j === i ? e.target.value : x)) })} />
                  {form.options.length > 2 && <button onClick={() => setForm({ ...form, options: form.options.filter((_, j) => j !== i) })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4 }}><Trash2 size={15} /></button>}
                </div>
              ))}
              <OutlineBtn onClick={() => setForm({ ...form, options: [...form.options, ''] })}><Plus size={13} style={{ marginRight: 4 }} /> Add option</OutlineBtn>
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={create} disabled={busy}>Create</PrimaryBtn></DialogActions>
      </Dialog>
    </div>
  );
}
